import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import './ProfileReputationCard.scss';

/**
 * Compact reputation surface on a public profile page.
 *
 * The Vision Roadmap v0.3 wedge is "reputation, not follower count,
 * decides who gets seen." Making that visible on the profile page —
 * to fans, buyers, and anyone considering the creator — is how the
 * signal becomes a first-class identity component, not just a
 * ranking input.
 *
 * Hits the same PUBLIC endpoint as any external integrator would
 * (`GET /api/public/reputation/:profileId`, no auth) so the numbers
 * here are the numbers the world sees. Anti-abuse readings are
 * already redacted at the API level per ADR-008 amendment
 * (v1.2, 2026-08-06); this component doesn't have to opt in.
 *
 * Renders nothing (returns null) when the profile isn't on-chain
 * yet or when no score has been computed — an empty state on a
 * profile card would just be noise for a brand-new creator.
 */

interface Props {
  /** On-chain integer profile id (nft_profile.profile_id). */
  profileId: number;
  /** When true, the current viewer owns this profile — we render a
   *  small "Explain" link to their analytics dashboard. Other viewers
   *  get the same numbers minus the shortcut. */
  isOwner?: boolean;
}

interface ScoreBlock {
  value: number;
  // v1.3 (2026-08-06): rank + percentile added server-side. Optional
  // in the client type so a stale client bundle against a new server
  // still renders (rank chip just won't show for those users).
  rank?: {
    position: number;
    total: number;
    percentile: number | null;
  };
}

interface ReputationSnapshot {
  scores: {
    klyxValue: ScoreBlock | null;
    trendingScore: ScoreBlock | null;
    hotScore: ScoreBlock | null;
  };
  formulaVersion: string;
  computedAt: string | null;
}

const ProfileReputationCard: React.FC<Props> = ({ profileId, isOwner }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<ReputationSnapshot | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<ReputationSnapshot>(
          `/public/reputation/${profileId}`,
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        // 404 or other — silently hide. This surface is opportunistic,
        // not load-bearing; a failed fetch shouldn't clutter the
        // profile with an error message.
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [profileId]);

  if (failed || !data) return null;

  const { scores } = data;
  const anyScore =
    scores.klyxValue !== null ||
    scores.trendingScore !== null ||
    scores.hotScore !== null;

  // Brand-new profile with no scores yet — hide entirely. The
  // "no scores" state is more distracting than useful on a public
  // profile card; the creator sees the "not computed yet" hint on
  // their own analytics dashboard.
  if (!anyScore) return null;

  const fmt = (v: number) => v.toFixed(2);
  // Card-friendly rank format — no "of N" here (that's a dashboard
  // detail); the profile card wants a single-glance signal. Prefer
  // percentile when the population passes the server threshold,
  // otherwise fall back to just the position number.
  const fmtRankBrief = (rank?: { position: number; percentile: number | null }): string | null => {
    if (!rank) return null;
    if (rank.percentile !== null) {
      const pct = Math.max(1, Math.round(rank.percentile * 100));
      return `Top ${pct}%`;
    }
    return `#${rank.position}`;
  };

  const klyxRank = scores.klyxValue ? fmtRankBrief(scores.klyxValue.rank) : null;

  return (
    <div className="profile-reputation">
      <div className="profile-reputation__row">
        <div className="profile-reputation__score">
          <span className="profile-reputation__score-label">Klyx Score</span>
          <span className="profile-reputation__score-value profile-reputation__score-value--primary">
            {scores.klyxValue ? fmt(scores.klyxValue.value) : '—'}
          </span>
          {/* Rank chip on the primary score only — the compact card
              stays clean if only one tile carries competitive signal. */}
          {klyxRank && (
            <span className="profile-reputation__score-rank">{klyxRank}</span>
          )}
          <span className="profile-reputation__score-blurb">Lifetime</span>
        </div>
        <div className="profile-reputation__score">
          <span className="profile-reputation__score-label">Trending</span>
          <span className="profile-reputation__score-value">
            {scores.trendingScore ? fmt(scores.trendingScore.value) : '—'}
          </span>
          <span className="profile-reputation__score-blurb">7 days</span>
        </div>
        <div className="profile-reputation__score">
          <span className="profile-reputation__score-label">Hot</span>
          <span className="profile-reputation__score-value">
            {scores.hotScore ? fmt(scores.hotScore.value) : '—'}
          </span>
          <span className="profile-reputation__score-blurb">Right now</span>
        </div>
      </div>
      <p className="profile-reputation__hint">
        Klyx reputation is portable — the same score is queryable via a public API
        and drives marketplace + discovery ranking. It's tied to the on-chain profile,
        not the follower count.
      </p>
      {isOwner && (
        <button
          type="button"
          className="profile-reputation__explain"
          onClick={() => navigate('/analytics?category=reputation')}
        >
          Explain my score →
        </button>
      )}
    </div>
  );
};

export default ProfileReputationCard;
